// =============================================================
// DamUpload.jsx — drag/drop upload modal for the DAM module.
// Uploads each file straight to Blob Storage (SAS), then confirms (which runs
// Claude auto-tagging server-side). Part of the removable DAM module.
// =============================================================
import { useState, useRef, useCallback } from 'react';
import { B, space, font, radius, weight } from '../../constants/brand';
import { Modal, Btn, Select, Icon, useToast } from '../../components/ui';
import { uploadAsset, formatBytes, categoryOf, iconForCategory, RAW_EDITED_UPLOAD_OPTIONS } from './damApi';
import { filesFromDrop, folderOfFile } from './folderDrop';

const STATUS_COLOR = { pending: B.textLt, uploading: B.amber, done: B.green, error: B.red };

export function DamUpload({ collections = [], defaultCollectionId = '', onClose, onUploaded }) {
  const { toast } = useToast();
  const [collectionId, setCollectionId] = useState(defaultCollectionId || '');
  // Raw vs. Edited, declared here for the whole batch. '' hands the question to
  // the AI tagger; anything else is a person's answer and the server will not
  // let the tagger overwrite it.
  const [rawEdited, setRawEdited] = useState('');
  const [items, setItems] = useState([]);      // { file, status, message }
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const folderInputRef = useRef(null);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setItems(prev => [...prev, ...files.map(file => ({ file, status: 'pending', message: '', progress: 0 }))]);
  }, []);

  // A dropped FOLDER is read through the File System Entry API — `dataTransfer
  // .files` is flat and on most browsers does not contain a folder's contents at
  // all, so dropping one used to add nothing. See folderDrop.js.
  const onDrop = useCallback(async (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(await filesFromDrop(e.dataTransfer));
  }, [addFiles]);

  // Upload (or resume) a single queued item. Block staging persists progress to
  // localStorage, so an errored item's Retry continues instead of restarting.
  const uploadOne = async (i, file) => {
    setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'uploading', message: '', progress: it.progress || 0 } : it));
    try {
      const { ai } = await uploadAsset(file, {
        collectionId: collectionId || null,
        rawEdited: rawEdited || null,
        onProgress: ({ loaded, total }) => {
          const p = total ? loaded / total : 0;
          setItems(prev => prev.map((it, idx) => {
            if (idx !== i) return it;
            if (it.progress != null && p < 1 && Math.abs(p - it.progress) < 0.01) return it;
            return { ...it, progress: p };
          }));
        },
      });
      const msg = ai?.applied && ai.tags?.length ? `AI tagged: ${ai.tags.slice(0, 4).join(', ')}${ai.tags.length > 4 ? '…' : ''}` : 'Uploaded';
      setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'done', message: msg, progress: 1 } : it));
      return true;
    } catch (e) {
      setItems(prev => prev.map((it, idx) => idx === i ? { ...it, status: 'error', message: e.message } : it));
      return false;
    }
  };

  const runUpload = async () => {
    if (!items.length || busy) return;
    setBusy(true);
    let uploaded = 0;
    for (let i = 0; i < items.length; i++) {
      if (items[i].status === 'done') continue;
      if (await uploadOne(i, items[i].file)) uploaded++;
    }
    setBusy(false);
    if (uploaded > 0) {
      toast.success(`Uploaded ${uploaded} asset${uploaded === 1 ? '' : 's'}`);
      onUploaded?.();
    }
  };

  const retryOne = async (i, file) => {
    if (busy) return;
    setBusy(true);
    const ok = await uploadOne(i, file);
    setBusy(false);
    if (ok) { toast.success('Uploaded 1 asset'); onUploaded?.(); }
  };

  const allDone = items.length > 0 && items.every(it => it.status === 'done');
  const collectionOptions = [
    { value: '', label: 'No collection' },
    ...collections.map(c => ({ value: c.collectionID, label: c.name })),
  ];

  return (
    <Modal title="Upload assets" onClose={onClose} width={640}>
      <div style={{ padding: space.xl, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 240px', minWidth: 200 }}>
            <Select label="Add to collection" value={collectionId} onChange={setCollectionId} options={collectionOptions} />
          </div>
          <div style={{ flex: '1 1 240px', minWidth: 200 }}>
            <Select label="These files are" value={rawEdited} onChange={setRawEdited}
              options={RAW_EDITED_UPLOAD_OPTIONS} />
            <div style={{ marginTop: 4, fontSize: font.xs, color: B.textLt, lineHeight: 1.4 }}>
              {rawEdited
                ? 'Applies to every file in this batch, and is kept — auto-tagging will not change it.'
                : 'Claude guesses from the image; a camera RAW or a design file is classified by its type.'}
            </div>
          </div>
        </div>

        {/* Dropzone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${dragging ? B.navy : B.border}`,
            background: dragging ? B.surfaceMuted : B.surfaceSunken,
            borderRadius: radius.xxl, padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            transition: 'border-color .15s, background .15s',
          }}
        >
          <Icon name="upload" size={28} color={B.navy} />
          <div style={{ marginTop: space.sm, fontWeight: weight.semibold, color: B.text }}>
            Drag & drop files or folders here, or click to browse
          </div>
          <div style={{ marginTop: 4, fontSize: font.sm, color: B.textMd }}>
            Images, video, audio, PDFs, docs and design files — Claude auto-tags images on upload.
          </div>
          <input ref={inputRef} type="file" multiple hidden
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          {/* A directory pick populates each File's `webkitRelativePath`, which
              damApi sends on so the folder a batch came in with is recorded
              (and read for raw/edited) instead of being flattened away. The
              dropzone's own click must not also open this one. */}
          <input ref={folderInputRef} type="file" multiple hidden webkitdirectory=""
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          <button type="button"
            onClick={(e) => { e.stopPropagation(); folderInputRef.current?.click(); }}
            style={{ marginTop: space.md, border: `1px solid ${B.border}`, background: B.surface,
              borderRadius: radius.lg, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit',
              fontSize: font.sm, fontWeight: weight.semibold, color: B.navy }}>
            Add a folder
          </button>
        </div>

        {/* Queue */}
        {items.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {items.map((it, i) => {
              const cat = categoryOf(it.file.name.split('.').pop());
              return (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: space.md, padding: '8px 12px',
                  background: B.surfaceMuted, borderRadius: radius.lg, border: `1px solid ${B.border}`,
                }}>
                  <Icon name={iconForCategory(cat)} size={18} color={B.textMd} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: font.base, color: B.text, fontWeight: weight.medium,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.file.name}</div>
                    {folderOfFile(it.file) && (
                      <div style={{ fontSize: font.xs, color: B.textLt, overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folderOfFile(it.file)}</div>
                    )}
                    <div style={{ fontSize: font.xs, color: it.status === 'error' ? B.red : B.textMd }}>
                      {it.status === 'uploading'
                        ? `${Math.round((it.progress || 0) * 100)}% · ${formatBytes((it.progress || 0) * it.file.size)} / ${formatBytes(it.file.size)}`
                        : `${formatBytes(it.file.size)}${it.message ? ` · ${it.message}` : ''}`}
                    </div>
                    {it.status === 'uploading' && (
                      <div style={{ marginTop: 5, height: 4, borderRadius: 2, background: B.border, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.round((it.progress || 0) * 100)}%`,
                          background: B.navy, transition: 'width .15s' }} />
                      </div>
                    )}
                  </div>
                  {it.status === 'error' && (
                    <button type="button" onClick={() => retryOne(i, it.file)} disabled={busy}
                      style={{ fontSize: font.xs, fontWeight: weight.semibold, color: B.navy, background: 'none',
                        border: 'none', cursor: busy ? 'default' : 'pointer', padding: '2px 4px' }}>Retry</button>
                  )}
                  <span style={{ fontSize: font.xs, fontWeight: weight.semibold, color: STATUS_COLOR[it.status],
                    textTransform: 'capitalize' }}>{it.status}</span>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.sm }}>
          <Btn variant="secondary" onClick={onClose}>{allDone ? 'Close' : 'Cancel'}</Btn>
          <Btn onClick={runUpload} disabled={!items.length || busy || allDone} loading={busy}>
            {busy ? 'Uploading…' : `Upload ${items.filter(i => i.status !== 'done').length || ''}`.trim()}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}
